import { Card, Col, InputNumber, Row, Select, notification } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import InsurancePlanIdccRuleSelect from "shared/components/InsurancePlanIdccRuleSelect";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  ENABLED_DIMENSIONS,
  slotKey,
  slotsToRules,
} from "./eligibility-dimensions";

const ConditionsManager = ({ params, planId, setFieldValue }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [updatePlan] = useMutation(graphql.mutations.UPDATE_INSURANCE_PLAN);
  const rules = get(params, "0.rules", {});

  const getValue = (name, predicate) =>
    get(rules, [slotKey(name, predicate), "value"]);

  const setValue = (name, predicate, value) => {
    const key = slotKey(name, predicate);
    const existing = get(rules, key, {});
    const nextParam = {
      ...get(params, "0"),
      id: get(params, "0.id") || cuid(),
      rules: {
        ...rules,
        [key]: {
          ...existing,
          id: existing.id || cuid(),
          name,
          predicate,
          value,
        },
      },
    };
    setFieldValue("params.0", nextParam);
    return nextParam;
  };

  const saveParams = async (param = get(params, "0")) => {
    if (!get(param, "id")) return;
    try {
      await updatePlan({
        variables: {
          where: { id: planId },
          data: {
            params: [{ id: param.id, rules: slotsToRules(param.rules) }],
          },
        },
      });
    } catch (err) {
      notification.error({
        message: "Échec de l'enregistrement des conditions",
      });
    }
  };

  const commit = (name, predicate, value) =>
    saveParams(setValue(name, predicate, value));

  const labelOf = (dim) => (dim.labelKey ? t(dim.labelKey) : dim.label);

  const renderControl = (dim) => {
    if (dim.widget === "multiselect")
      return (
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder={dim.placeholder}
          showSearch={dim.showSearch}
          maxTagCount="responsive"
          options={dim.options}
          value={getValue(dim.dimension, dim.predicate) || []}
          onChange={(value) => commit(dim.dimension, dim.predicate, value)}
        />
      );

    if (dim.widget === "select")
      return (
        <Select
          style={{ width: "100%" }}
          placeholder={dim.placeholder}
          allowClear
          options={dim.options}
          value={getValue(dim.dimension, dim.predicate) || undefined}
          onChange={(value) =>
            commit(dim.dimension, dim.predicate, value || "")
          }
        />
      );

    if (dim.widget === "tags")
      return (
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder={dim.placeholder}
          maxTagCount="responsive"
          tokenSeparators={[",", " "]}
          value={getValue(dim.dimension, dim.predicate) || []}
          onChange={(value) => {
            const valid = dim.tagPattern
              ? value.filter((v) => dim.tagPattern.test(v))
              : value;
            if (valid.length !== value.length)
              notification.warning({ message: dim.tagError });
            commit(dim.dimension, dim.predicate, valid);
          }}
        />
      );

    if (dim.widget === "idcc")
      return (
        <InsurancePlanIdccRuleSelect
          placeholder={t(dim.placeholderKey)}
          value={getValue(dim.dimension, dim.predicate) || []}
          onChange={(next) => commit(dim.dimension, dim.predicate, next)}
        />
      );

    return null;
  };

  const renderRow = (dim) => {
    if (dim.widget === "numberRange")
      return (
        <Row gutter={[16, 16]} align="middle" key={dim.dimension}>
          <Col span={6}>
            <label className="input__label">{labelOf(dim)}</label>
          </Col>
          {dim.bounds.map((bound) => (
            <Col span={9} key={bound.predicate}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                addonBefore={bound.addon}
                value={getValue(dim.dimension, bound.predicate)}
                onChange={(val) =>
                  setValue(dim.dimension, bound.predicate, val)
                }
                onBlur={() => saveParams()}
              />
            </Col>
          ))}
        </Row>
      );

    return (
      <Row gutter={[16, 16]} align="middle" key={dim.dimension}>
        <Col span={6}>
          <label className="input__label">{labelOf(dim)}</label>
        </Col>
        <Col span={18}>{renderControl(dim)}</Col>
      </Row>
    );
  };

  return (
    <Card title="Conditions d'adhésion" className="m-bottom-16">
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {ENABLED_DIMENSIONS.map(renderRow)}
      </div>
    </Card>
  );
};

export default ConditionsManager;
